import React from "react";
import { DV2 } from "../theme";
import { euros, hours as formatHours } from "../format";
import { Card, FieldLabel, Hint, KeyValue, useDv2Theme } from "../layout/Ui";
import { RelationRemuneration, relationRateHint } from "./relationTeachr";

/**
 * Client en relation Teach'r : le tarif du prof est fixé par la plateforme et
 * le prof ne voit que sa part. Deux blocs remplacent le champ de prix et le
 * bloc de montants : le tarif horaire verrouillé, et la rémunération totale.
 * Aucun prix total, aucune avance immédiate, aucun frais de gestion.
 */

interface RateProps {
    remuneration: RelationRemuneration;
    firstName: string;
    /** Message d'erreur (plafond du mois dépassé). */
    error?: string | null;
}

/** Tarif horaire du prof, verrouillé, à la place du champ « Prix horaire payé ». */
export const RelationRateField = ({ remuneration, firstName, error }: RateProps) => {
    const { fontFamily, brandName } = useDv2Theme();
    return (
        <div>
            <FieldLabel>Votre tarif horaire</FieldLabel>
            <div style={{ fontFamily, fontSize: 20, fontWeight: 800, padding: "10px 0 4px" }} aria-live="polite">
                {euros(remuneration.hourly)} <span style={{ fontSize: 14, fontWeight: 700, color: DV2.muted }}>par heure</span>
            </div>
            {error
                ? <div role="alert" style={{ fontSize: 13, color: DV2.danger, fontWeight: 700, lineHeight: 1.45 }}>{error}</div>
                : <Hint>{relationRateHint(brandName, firstName)}</Hint>}
        </div>
    );
};

interface BlockProps {
    remuneration: RelationRemuneration;
    /** « pour ces 3 séances » / « pour ce cours ». */
    scope: string;
}

/** Rémunération totale, tarif horaire fois heures, à la place du bloc de montants. */
export const RelationRemunerationBlock = ({ remuneration, scope }: BlockProps) => {
    const { primary, fontFamily, brandName } = useDv2Theme();
    const r = remuneration;
    const ready = r.hourly > 0 && r.hours > 0;
    return (
        <Card padding={0} style={{ overflow: "hidden", fontFamily }} aria-label="Rémunération">
            <div style={{ padding: 22, background: primary, color: "#fff", display: "flex", flexDirection: "column", gap: 4 }}>
                <div style={{ fontSize: 13, fontWeight: 700, opacity: 0.85 }}>Votre rémunération {scope}</div>
                <div style={{ fontSize: 38, fontWeight: 800, lineHeight: 1.1 }} aria-live="polite">{ready ? euros(r.total) : "…"}</div>
                <div style={{ fontSize: 13, opacity: 0.85, lineHeight: 1.45 }}>
                    {ready ? <>soit {euros(r.hourly)} par heure sur {formatHours(r.hours)}, reversés par {brandName}.</> : "Ajoutez vos séances pour connaître le montant."}
                </div>
            </div>
            <div style={{ padding: "20px 22px", display: "flex", flexDirection: "column", gap: 14, fontSize: 15 }}>
                <KeyValue label="Rémunération horaire" value={euros(r.hourly)} />
                <KeyValue label="Heures" value={formatHours(r.hours)} />
                <div style={{ paddingTop: 12, borderTop: `1px solid ${DV2.borderSoft}` }}>
                    <KeyValue label="Rémunération totale" value={<b>{ready ? euros(r.total) : "…"}</b>} />
                </div>
            </div>
        </Card>
    );
};
